import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Plus, Search, Play, X, ArrowLeft, Filter, 
    CheckCircle, Clock, AlertCircle, Ban, Loader2, Info
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';

const Production = () => {
    const navigate = useNavigate();
    const [productions, setProductions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('TOUS');

    // État pour la modale
    const [modal, setModal] = useState({
        isOpen: false,
        type: 'confirm', // 'confirm' ou 'alert'
        title: '',
        message: '',
        onConfirm: null,
        loading: false
    });

    const colors = {
        primary: '#b45309', secondary: '#f59e0b', light: '#fef3c7',
        dark: '#78350f', bg: '#fffbeb', white: '#ffffff',
        success: '#10b981', info: '#3b82f6', danger: '#ef4444'
    };

    const s = {
        container: { display: 'flex', height: '100vh', background: colors.white },
        mainArea: { 
            flex: 1, display: 'flex', flexDirection: 'column', 
            marginLeft: '260px', background: colors.bg, overflowX: 'hidden' 
        },
        topBar: { 
            padding: '25px 40px', background: colors.white, 
            borderBottom: `4px solid ${colors.primary}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' 
        },
        filterBar: {
            padding: '15px 40px', display: 'flex', gap: '10px', alignItems: 'center',
            background: 'rgba(255,255,255,0.5)', borderBottom: '1px solid #e2e8f0'
        },
        filterBtn: (active, color) => ({
            padding: '8px 16px', borderRadius: '20px', border: active ? 'none' : `1px solid ${color}`,
            background: active ? color : 'transparent', color: active ? 'white' : color,
            fontWeight: '800', fontSize: '11px', cursor: 'pointer', display: 'flex', 
            alignItems: 'center', gap: '6px', transition: 'all 0.2s'
        }),
        tableContainer: { padding: '30px 40px', flex: 1 },
        table: { width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' },
        th: { padding: '15px', color: colors.dark, fontSize: '12px', fontWeight: '900', textAlign: 'left', textTransform: 'uppercase' },
        tr: { background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', borderRadius: '10px' },
        td: { padding: '15px', color: '#334155' },
        statusBadge: (status) => {
            const configs = {
                'PLANIFIE': { bg: '#fef3c7', color: '#b45309' },
                'EN_COURS': { bg: '#dbeafe', color: '#1e40af' },
                'TERMINEE': { bg: '#d1fae5', color: '#065f46' },
                'ANNULEE': { bg: '#fee2e2', color: '#991b1b' }
            };
            const config = configs[status] || { bg: '#f1f5f9', color: '#475569' };
            return {
                padding: '6px 12px', borderRadius: '8px', fontSize: '10px', 
                fontWeight: '900', background: config.bg, color: config.color
            };
        },
        modalOverlay: {
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
            justifyContent: 'center', alignItems: 'center', zIndex: 1000,
            backdropFilter: 'blur(4px)'
        },
        modalContent: {
            background: 'white', padding: '30px', borderRadius: '20px',
            width: '400px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
            textAlign: 'center'
        }
    };

    useEffect(() => {
        fetchProductions(); 
        const handleGlobalUpdate = (event) => {
            const { table } = event.detail;
            if (table === 'production_needs' || table === 'all' || table === 'productions') {
                fetchProductions(); 
            }
        };
        window.addEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
        return () => window.removeEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
    }, []);

    const fetchProductions = async () => {
        try {
            setLoading(true);
            const response = await API.get('/production/list');
            setProductions(response.data || []);
        } catch (err) {
            showAlert("Erreur de chargement", "Impossible de récupérer la liste des productions.");
            setProductions([
                { id: 'OF-882731', date_production: new Date(), article_nom: 'Pain Complet', quantite_prevue: 50, statut: 'EN_COURS' },
                { id: 'OF-992102', date_production: new Date(), article_nom: 'Croissant Beurre', quantite_prevue: 120, statut: 'PLANIFIE' }
            ]);
        } finally {
            setLoading(false);
        }
    };

    const showAlert = (title, message) => {
        setModal({ isOpen: true, type: 'alert', title, message, onConfirm: null, loading: false });
    };

    const confirmAction = (title, message, action) => {
        setModal({ isOpen: true, type: 'confirm', title, message, onConfirm: action, loading: false });
    };

    const handleUpdateStatus = async (id, newStatus) => {
        const title = newStatus === 'EN_COURS' ? "Lancer la production ?" : "Terminer la production ?";
        const message = newStatus === 'TERMINEE' 
            ? "Cette action va clôturer l'ordre et mettre à jour vos stocks de produits finis." 
            : "Voulez-vous démarrer la fabrication de cet article ?";
        confirmAction(title, message, async () => {
            setModal(prev => ({ ...prev, loading: true }));
            try {
                await API.put(`/production/update-status/${id}`, { status: newStatus });
                setModal({ isOpen: false }); 
                fetchProductions();
            } catch (err) {
                setModal({ isOpen: false });
                showAlert("Erreur", err.response?.data?.error || "L'opération a échoué.");
            }
        });
    };

    const filtered = productions.filter(p => {
        const statusMap = { 'ORDRE': 'PLANIFIE', 'EN_COURS': 'EN_COURS', 'TERMINE': 'TERMINEE', 'ANNULE': 'ANNULEE' };
        const targetStatus = statusMap[filterStatus];
        const matchesStatus = filterStatus === 'TOUS' || p.statut === targetStatus;
        return matchesStatus && (
            (p.id && p.id.toLowerCase().includes(searchTerm.toLowerCase())) || 
            (p.article_nom && p.article_nom.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    });

    return (
        <div style={s.container}>
            <Sidebar />
            
            {/* MODALE PERSONNALISÉE */}
            {modal.isOpen && (
                <div style={s.modalOverlay}>
                    <div style={s.modalContent}>
                        <div style={{ marginBottom: '15px' }}>
                            {modal.type === 'confirm' ? (
                                <HelpCircle size={48} color={colors.secondary} style={{ margin: '0 auto' }} />
                            ) : (
                                <AlertCircle size={48} color={colors.danger} style={{ margin: '0 auto' }} />
                            )}
                        </div>
                        <h3 style={{ color: colors.dark, margin: '0 0 10px 0', fontWeight: '900' }}>{modal.title}</h3>
                        <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '25px', lineHeight: '1.5' }}>{modal.message}</p>
                        
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            {modal.type === 'confirm' && (
                                <button 
                                    disabled={modal.loading}
                                    onClick={() => setModal({ ...modal, isOpen: false })}
                                    style={{ padding: '10px 20px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', fontWeight: '800', cursor: 'pointer' }}
                                >
                                    ANNULER
                                </button>
                            )}
                            <button 
                                disabled={modal.loading}
                                onClick={modal.type === 'confirm' ? modal.onConfirm : () => setModal({ ...modal, isOpen: false })}
                                style={{ 
                                    padding: '10px 25px', borderRadius: '10px', border: 'none', 
                                    background: modal.type === 'confirm' ? colors.primary : colors.dark, 
                                    color: 'white', fontWeight: '800', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: '8px'
                                }}
                            >
                                {modal.loading ? <Loader2 size={16} className="animate-spin" /> : (modal.type === 'confirm' ? 'CONFIRMER' : 'OK')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <main style={s.mainArea}>
                <div style={s.topBar}>
                    <div>
                        <h2 style={{ color: colors.dark, fontWeight: '900', margin: 0, fontSize: '24px' }}>TABLEAU DE BORD CUISINE</h2>
                        <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>Suivi temps réel de la production et des stocks (par lots et rendement)</p>
                    </div>
                    <button 
                        onClick={() => navigate('/production/nouveau')}
                        style={{ background: colors.primary, color: 'white', border: 'none', padding: '12px 25px', borderRadius: '12px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: `0 4px 14px ${colors.primary}44` }}
                    >
                        <Plus size={20} /> CRÉER PRODUCTION
                    </button>
                </div>

                <div style={s.filterBar}>
                    <span style={{ fontSize: '11px', fontWeight: '900', color: colors.dark, marginRight: '10px' }}>FILTRER PAR :</span>
                    <button onClick={() => setFilterStatus('TOUS')} style={s.filterBtn(filterStatus === 'TOUS', '#64748b')}><Filter size={14}/> TOUS</button>
                    <button onClick={() => setFilterStatus('ORDRE')} style={s.filterBtn(filterStatus === 'ORDRE', colors.secondary)}><Clock size={14}/> ORDRES</button>
                    <button onClick={() => setFilterStatus('EN_COURS')} style={s.filterBtn(filterStatus === 'EN_COURS', colors.info)}><Play size={14}/> EN COURS</button>
                    <button onClick={() => setFilterStatus('TERMINE')} style={s.filterBtn(filterStatus === 'TERMINE', colors.success)}><CheckCircle size={14}/> TERMINÉS</button>
                    
                    <div style={{ marginLeft: 'auto', position: 'relative', width: '300px' }}>
                        <Search style={{ position: 'absolute', left: '12px', top: '10px', color: '#94a3b8' }} size={16} />
                        <input 
                            style={{ width: '100%', padding: '8px 15px 8px 40px', borderRadius: '20px', border: '1px solid #e2e8f0', outline: 'none', fontSize: '13px' }}
                            placeholder="Rechercher OF ou Article..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                <div style={s.tableContainer}>
                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}><Loader2 className="animate-spin" size={40} color={colors.primary} /></div>
                    ) : (
                        <table style={s.table}>
                            <thead>
                                <tr>
                                    <th style={s.th}>Référence / Date</th>
                                    <th style={s.th}>Article à Fabriquer</th>
                                    <th style={{ ...s.th, textAlign: 'center' }}>Quantité Totale Prévue</th>
                                    <th style={{ ...s.th, textAlign: 'center' }}>État Actuel</th>
                                    <th style={{ ...s.th, textAlign: 'right' }}>Actions Production</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(prod => (
                                    <tr key={prod.id} style={s.tr}>
                                        <td style={{ ...s.td, borderTopLeftRadius: '10px', borderBottomLeftRadius: '10px' }}>
                                            <div style={{ fontWeight: '900', color: colors.primary }}>{prod.id}</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>{prod.date_production ? new Date(prod.date_production).toLocaleDateString() : '---'}</div>
                                        </td>
                                        <td style={{ ...s.td, fontWeight: '800' }}>{prod.article_nom}</td>
                                        <td style={{ ...s.td, textAlign: 'center', fontWeight: '900', fontSize: '16px' }}>{prod.quantite_prevue || prod.quantite || 0}</td>
                                        <td style={{ ...s.td, textAlign: 'center' }}>
                                            <span style={s.statusBadge(prod.statut)}>
                                                {(prod.statut || 'PLANIFIE').replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td style={{ ...s.td, textAlign: 'right', borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }}>
                                            {prod.statut === 'PLANIFIE' && (
                                                <button 
                                                    onClick={() => handleUpdateStatus(prod.id, 'EN_COURS')}
                                                    style={{ background: colors.info, color: 'white', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}
                                                >
                                                    <Play size={16} /> LANCER
                                                </button>
                                            )}
                                            {prod.statut === 'EN_COURS' && (
                                                <button 
                                                    onClick={() => handleUpdateStatus(prod.id, 'TERMINEE')}
                                                    style={{ background: colors.success, color: 'white', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}
                                                >
                                                    <CheckCircle size={16} /> TERMINER
                                                </button>
                                            )}
                                            {prod.statut === 'TERMINEE' && (
                                                <span style={{ color: colors.success, fontWeight: '900', fontSize: '12px' }}>PRODUIT PRÊT ✓</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={{ padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white', borderTop: '1px solid #e2e8f0' }}>
                    <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'none', border: 'none', fontWeight: '800', color: '#64748b', cursor: 'pointer' }}>
                        <ArrowLeft size={18}/> RETOUR
                    </button>
                    <div style={{ color: '#94a3b8', fontSize: '12px', fontWeight: '700' }}>
                        PRODUCTIONS EN ATTENTE : {productions.filter(p => p.statut === 'PLANIFIE').length}
                    </div>
                </div>
            </main>
        </div>
    );
};

const HelpCircle = ({ size, color, style }) => (
    <svg style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

export default Production;